import mansoqConfig from "@mansoqConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { navigate, usePath } from "raviger";
import { useCallback, useEffect, useRef, useState } from "react";

import Loading from "@/components/Common/Loading";

import { AuthUserContext } from "@/hooks/useAuthUser";

import { LocalStorageKeys } from "@/common/constants";

import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import { userAtom } from "@/atoms/user-atom";
import {
  JwtTokenObtainPair,
  LoginResponse,
  MfaAuthenticationToken,
} from "@/types/auth/auth";
import authApi from "@/types/auth/authApi";
import { TokenData } from "@/types/otp/otp";
import { CurrentUserRead } from "@/types/user/user";
import userApi from "@/types/user/userApi";

interface Props {
  children: React.ReactNode;
  unauthorized: React.ReactNode;
  otpAuthorized: React.ReactNode;
}

const isMFAResponse = (data: LoginResponse): data is MfaAuthenticationToken => {
  return "temp_token" in data;
};

const isJwtTokenResponse = (
  data: LoginResponse,
): data is JwtTokenObtainPair => {
  return "access" in data && "refresh" in data;
};

interface StoredTokenPair {
  access: string | null;
  refresh: string | null;
}

interface VerifiedSessionUser {
  generation: number;
  user: CurrentUserRead;
}

interface AuthSession extends StoredTokenPair {
  generation: number;
  continuation?: VerifiedSessionUser & { accessToken: string };
}

// Opaque client session scope: never infer identity from unverified JWT claims.
let nextSessionGeneration = 0;

const readTokenPair = (): StoredTokenPair => ({
  access: localStorage.getItem(LocalStorageKeys.accessToken),
  refresh: localStorage.getItem(LocalStorageKeys.refreshToken),
});

const sameTokenPair = (left: StoredTokenPair, right: StoredTokenPair) =>
  left.access === right.access && left.refresh === right.refresh;

export default function AuthUserProvider({
  children,
  unauthorized,
  otpAuthorized,
}: Props) {
  const queryClient = useQueryClient();
  const [
    { access: accessToken, refresh: refreshToken, generation, continuation },
    setTokenPair,
  ] = useState<AuthSession>(() => ({
    ...readTokenPair(),
    generation: ++nextSessionGeneration,
  }));
  const verifiedUser = useRef<VerifiedSessionUser | undefined>(undefined);
  const sessionRevision = useRef(0);
  const active = useRef(true);

  const applyTokenPair = useCallback(
    (
      pair: StoredTokenPair,
      writeStorage = true,
      continuingUser?: VerifiedSessionUser,
    ) => {
      sessionRevision.current++;
      const nextGeneration = ++nextSessionGeneration;
      if (!continuingUser) verifiedUser.current = undefined;
      if (writeStorage) {
        if (pair.access === null)
          localStorage.removeItem(LocalStorageKeys.accessToken);
        else localStorage.setItem(LocalStorageKeys.accessToken, pair.access);
        if (pair.refresh === null)
          localStorage.removeItem(LocalStorageKeys.refreshToken);
        else localStorage.setItem(LocalStorageKeys.refreshToken, pair.refresh);
      }
      setTokenPair((previous) => {
        const keepUser =
          !!pair.access && continuingUser?.generation === previous.generation;
        return {
          ...pair,
          generation: keepUser ? previous.generation : nextGeneration,
          continuation: keepUser
            ? { ...continuingUser!, accessToken: pair.access! }
            : undefined,
        };
      });
    },
    [],
  );
  const isCurrentSession = useCallback(
    (revision: number, pair: StoredTokenPair) =>
      active.current &&
      revision === sessionRevision.current &&
      sameTokenPair(readTokenPair(), pair),
    [],
  );
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const path = usePath();
  const [patientToken, setPatientToken] = useState<TokenData | null>(
    JSON.parse(
      localStorage.getItem(LocalStorageKeys.patientTokenKey) || "null",
    ),
  );

  const {
    data: currentUser,
    isLoading,
    isError,
    isSuccess,
    isPlaceholderData,
  } = useQuery<CurrentUserRead>({
    queryKey: ["currentUser", generation, accessToken],
    queryFn: async ({ signal }) => {
      const pair = { access: accessToken, refresh: refreshToken };
      const revision = sessionRevision.current;
      try {
        return await query(userApi.currentUser, { silent: true })({ signal });
      } catch (error) {
        if (!signal.aborted && isCurrentSession(revision, pair)) {
          if (verifiedUser.current?.generation === generation)
            verifiedUser.current = undefined;
          setTokenPair((previous) =>
            previous.generation === generation &&
            previous.access === accessToken
              ? { ...previous, continuation: undefined }
              : previous,
          );
        }
        throw error;
      }
    },
    retry: false,
    enabled: !!accessToken,
    // Only a guarded successful refresh may carry a server-verified user,
    // and only for its exact newly applied access token in the same session.
    placeholderData: () =>
      continuation?.generation === generation &&
      continuation.accessToken === accessToken
        ? continuation.user
        : undefined,
  });
  // Query may retain successful cached data after a refetch error. Never keep
  // the authenticated subtree (or continuity) on any currentUser failure.
  const user = isError ? undefined : currentUser;
  useEffect(() => {
    if (isError) verifiedUser.current = undefined;
    else if (isSuccess && !isPlaceholderData && currentUser)
      verifiedUser.current = { generation, user: currentUser };
  }, [currentUser, generation, isError, isSuccess, isPlaceholderData]);

  const setUser = useSetAtom(userAtom);
  useEffect(() => {
    setUser(user);
  }, [user, setUser]);
  useQuery({
    // Keep one polling query: rotating a refresh token must not create a new
    // immediately fetching query on every successful refresh.
    queryKey: ["user-refresh-token"],
    // Poll explicitly at the configured interval, rather than on the temporary
    // enabled transition while a rotated access token reloads currentUser.
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const pair = { access: accessToken, refresh: refreshToken };
      const revision = sessionRevision.current;
      if (!isCurrentSession(revision, pair)) return null;
      try {
        const tokens = await query(authApi.tokenRefresh, {
          body: { refresh: refreshToken || "" },
        })({ signal });
        if (!signal.aborted && isCurrentSession(revision, pair)) {
          const state = queryClient.getQueryState([
            "currentUser",
            generation,
            accessToken,
          ]);
          const sameSessionUser = verifiedUser.current;
          applyTokenPair(
            tokens,
            true,
            state &&
              state.status !== "error" &&
              sameSessionUser?.generation === generation
              ? sameSessionUser
              : undefined,
          );
        }
        return tokens;
      } catch (error) {
        // A completed request from a replaced session cannot sign out or
        // redirect the new user through the global query error handler.
        if (signal.aborted || !isCurrentSession(revision, pair)) return null;
        applyTokenPair({ access: null, refresh: null });
        throw error;
      }
    },
    refetchIntervalInBackground: true,
    refetchInterval: mansoqConfig.auth.tokenRefreshInterval,
    enabled: !!refreshToken && !!user,
  });

  const { mutateAsync: signIn, isPending: isAuthenticating } = useMutation({
    mutationFn: mutate(authApi.login),
    onSuccess: async (data: LoginResponse) => {
      queryClient.invalidateQueries({ queryKey: ["enabled-plugins"] });
      if (isMFAResponse(data)) {
        localStorage.setItem("mfa_temp_token", data.temp_token);
        const redirectURL = getRedirectURL();
        const directURL = path !== "/login" ? window.location.href : null;
        navigate(
          redirectURL
            ? `/2fa?redirect=${redirectURL}`
            : directURL
              ? `/2fa?redirect=${directURL}`
              : "/2fa",
        );
        return;
      }

      if (isJwtTokenResponse(data)) {
        applyTokenPair(data);
        const revision = sessionRevision.current;

        await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
        if (
          isCurrentSession(revision, data) &&
          (path === "/" || path === "/login")
        ) {
          navigate(getRedirectOr("/"));
        }
      }
    },
  });

  const { mutateAsync: verifyMFA, isPending: isVerifyingMFA } = useMutation({
    mutationFn: mutate(authApi.mfa.login),
    onSuccess: async (data: JwtTokenObtainPair) => {
      localStorage.removeItem("mfa_temp_token");

      applyTokenPair(data);
      const revision = sessionRevision.current;

      await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      if (isCurrentSession(revision, data)) navigate(getRedirectOr("/"));
    },
  });

  const patientLogin = (tokenData: TokenData, redirectUrl: string) => {
    applyTokenPair(readTokenPair(), false);
    setPatientToken(tokenData);
    localStorage.setItem(
      LocalStorageKeys.patientTokenKey,
      JSON.stringify(tokenData),
    );
    navigate(redirectUrl);
  };

  const signOut = useCallback(async () => {
    const pair = readTokenPair();
    // callApi constructs the authenticated request synchronously before its
    // first await. Dispatch this exact pair before clearing local state.
    const logout =
      pair.access && pair.refresh
        ? mutate(authApi.logout)({ access: pair.access, refresh: pair.refresh })
        : Promise.resolve();
    applyTokenPair({ access: null, refresh: null });
    localStorage.removeItem(LocalStorageKeys.patientTokenKey);
    setPatientToken(null);
    void queryClient.cancelQueries({ queryKey: ["user-refresh-token"] });
    const reset = queryClient.resetQueries({ queryKey: ["currentUser"] });
    const redirectURL = getRedirectURL();
    navigate(redirectURL ? `/login?redirect=${redirectURL}` : "/login");
    try {
      await logout;
    } catch (error) {
      console.error("Error during logout:", error);
    }
    await reset;
  }, [applyTokenPair, queryClient]);

  // Adopt the current stored session, including a positive cross-tab login.
  // Storage events must not send a logout for whichever user logged in later.
  useEffect(() => {
    const listener = (event: StorageEvent) => {
      if (
        event.storageArea === localStorage &&
        (event.key === null ||
          [
            LocalStorageKeys.accessToken,
            LocalStorageKeys.refreshToken,
            LocalStorageKeys.patientTokenKey,
          ].includes(event.key))
      ) {
        const pair = readTokenPair();
        const nextPatientToken: TokenData | null = JSON.parse(
          localStorage.getItem(LocalStorageKeys.patientTokenKey) || "null",
        );
        applyTokenPair(pair, false);
        setPatientToken(nextPatientToken);
        if (!pair.access && !nextPatientToken?.token) {
          void queryClient.resetQueries({ queryKey: ["currentUser"] });
          const redirectURL = getRedirectURL();
          navigate(redirectURL ? `/login?redirect=${redirectURL}` : "/login");
        }
      }
    };

    addEventListener("storage", listener);

    return () => {
      removeEventListener("storage", listener);
    };
  }, [applyTokenPair, queryClient]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <AuthUserContext.Provider
      key={JSON.stringify([generation, user?.id ?? null])}
      value={{
        signIn,
        signOut,
        verifyMFA,
        isAuthenticating,
        isVerifyingMFA,
        user,
        patientLogin,
        patientToken,
      }}
    >
      {user ? children : patientToken?.token ? otpAuthorized : unauthorized}
    </AuthUserContext.Provider>
  );
}

const getRedirectURL = () => {
  return new URLSearchParams(window.location.search).get("redirect");
};

const getRedirectOr = (fallback: string) => {
  const url = getRedirectURL();

  if (url) {
    try {
      const redirect = new URL(url);
      if (window.location.origin === redirect.origin) {
        return redirect.pathname + redirect.search;
      }
      console.error("Redirect does not belong to same origin.");
    } catch {
      console.error(`Invalid redirect URL: ${url}`);
    }
  }

  return fallback;
};
